import * as React from 'react';
import cx from 'classnames';
import { map, capitalize } from 'lodash';

import { Tooltip } from 'antd';
import { EditorState } from 'draft-js';
import { StrikethroughIcon, CodeIcon } from '@revfluence/fresh-icons/regular/esm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { faBold, faItalic, faUnderline } from '@fortawesome/free-solid-svg-icons';
import { TInlineStyle } from './types';

const INLINE_STYLE_ICON: {
  [key in TInlineStyle]: React.ReactNode;
} = {
  BOLD: <FontAwesomeIcon icon={faBold} className="h-3 w-3 text-primary" />,
  ITALIC: <FontAwesomeIcon icon={faItalic} className="h-3 w-3 text-primary" />,
  UNDERLINE: <FontAwesomeIcon icon={faUnderline} className="h-3 w-3 text-primary" />,
  STRIKETHROUGH: <StrikethroughIcon width={14} height={14} />,
  CODE: <CodeIcon width={14} height={14} />,
};
const INLINE_STYLE_HOTKEY: {
  [key in TInlineStyle]: string;
} = {
  BOLD: '⌘B',
  ITALIC: '⌘I',
  UNDERLINE: '⌘U',
  STRIKETHROUGH: '⌘⇧X',
  CODE: '⌘E',
};

interface IProps {
  editorState: EditorState;
  toggleInlineStyle(style: TInlineStyle): void;

  className?: string;
}

/**
 * @type {React.FC}
 */
export const InlineStyles: React.FC<IProps> = React.memo((props) => {
  const { editorState } = props;
  const inlineStyles = editorState.getCurrentInlineStyle();

  // Only show basic formatting: Bold, Italic, Underline
  const availableStyles = ['BOLD', 'ITALIC', 'UNDERLINE'] as TInlineStyle[];

  return (
    <div className={cx('flex items-center gap-1', props.className)}>
      {map(availableStyles, (style) => (
        <Tooltip
          key={style}
          title={(
            <div className="flex items-center gap-3 font-medium text-xs">
              {capitalize(style)}
              <span className="bg-gray-600 text-white px-1.5 py-0.5 rounded text-xs">
                {INLINE_STYLE_HOTKEY[style]}
              </span>
            </div>
          )}
        >
          <div
            className={cx(
              'flex items-center justify-center w-6 h-6 rounded cursor-pointer transition-all duration-150 hover:bg-grey-2',
              {
                'bg-gray-100 text-blue-600': inlineStyles.has(style),
              }
            )}
            onMouseDown={(event) => {
              event.preventDefault();

              props.toggleInlineStyle(style);
            }}
          >
            {INLINE_STYLE_ICON[style]}
          </div>
        </Tooltip>
      ))}
    </div>
  );
});

InlineStyles.displayName = 'InlineStyles';
